import { cn } from "@design-system/utils";
import useEmblaCarousel from "embla-carousel-react";
import React, { type ReactNode, useCallback, useEffect, useState } from "react";

// Simple arrow icons (replace with your design system icons if available)
function ArrowLeft(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" {...props}>
      <path
        d="M13 15l-5-5 5-5"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
function ArrowRight(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" {...props}>
      <path
        d="M7 5l5 5-5 5"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function isRTL() {
  if (typeof window !== "undefined") {
    return document.dir === "rtl";
  }
  return false;
}

type CarouselProps = {
  children: ReactNode;
  className?: string;
  slidesPerView?: number;
};

export function Carousel({
  children,
  className,
  slidesPerView = 1,
}: CarouselProps) {
  const childArray = React.Children.toArray(children);
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: childArray.length > slidesPerView,
    slidesToScroll: 1,
    containScroll: "trimSnaps",
    dragFree: false,
    direction: isRTL() ? "rtl" : "ltr",
  });
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setCanScrollPrev(emblaApi.canScrollPrev());
    setCanScrollNext(emblaApi.canScrollNext());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
    return () => {
      emblaApi.off("select", onSelect);
      emblaApi.off("reInit", onSelect);
    };
  }, [emblaApi, onSelect]);

  const scrollPrev = useCallback(
    () => emblaApi && emblaApi.scrollPrev(),
    [emblaApi],
  );
  const scrollNext = useCallback(
    () => emblaApi && emblaApi.scrollNext(),
    [emblaApi],
  );

  const rtl = isRTL();

  return (
    <div className={cn("relative flex w-full items-center gap-2", className)}>
      {/* Arrows outside carousel, swap logic in RTL */}
      {childArray.length > slidesPerView && (
        <button
          type="button"
          aria-label={rtl ? "Next" : "Previous"}
          onClick={rtl ? scrollNext : scrollPrev}
          disabled={rtl ? !canScrollNext : !canScrollPrev}
          className="z-10 rounded-full bg-white p-1.5 text-gray-700 shadow hover:bg-gray-100 disabled:opacity-40 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
        >
          {rtl ? (
            <ArrowRight className="h-6 w-6" />
          ) : (
            <ArrowLeft className="h-6 w-6" />
          )}
        </button>
      )}
      <div
        className="embla__viewport w-full overflow-x-hidden py-1"
        ref={emblaRef}
      >
        <div className="embla__container flex">
          {childArray.map((child, idx) => (
            <div
              className="embla__slide min-w-0 flex-none px-2"
              style={{ width: `${100 / slidesPerView}%` }}
              key={idx}
            >
              {child}
            </div>
          ))}
        </div>
      </div>
      {childArray.length > slidesPerView && (
        <button
          type="button"
          aria-label={rtl ? "Previous" : "Next"}
          onClick={rtl ? scrollPrev : scrollNext}
          disabled={rtl ? !canScrollPrev : !canScrollNext}
          className="z-10 rounded-full bg-white p-1.5 text-gray-700 shadow hover:bg-gray-100 disabled:opacity-40 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
        >
          {rtl ? (
            <ArrowLeft className="h-6 w-6" />
          ) : (
            <ArrowRight className="h-6 w-6" />
          )}
        </button>
      )}
    </div>
  );
}
